<script setup>
import { computed, onMounted, ref } from 'vue';

const organizations = ref([]);
const selectedId = ref('');
const isLoading = ref(false);
const isSaving = ref(false);
const message = ref('');
const form = ref(emptyForm());

function emptyForm() {
  return {
    name: '',
    slug: '',
    status: 'active',
    timezone: 'Europe/Lisbon',
    currency: 'EUR',
    chatwootAccountId: '',
    chatwootInboxId: '',
    evolutionInstanceName: '',
    classificationCombo: '',
    conversationCombo: '',
    summaryCombo: '',
    imageSimulationCombo: '',
  };
}

const selected = computed(() =>
  organizations.value.find(item => item.id === selectedId.value)
);

function binding(item, channel) {
  return item.bindings?.find(value => value.channel === channel) || {};
}

async function load() {
  isLoading.value = true;
  message.value = '';
  try {
    const response = await fetch('/api/superwoot/organizations');
    const payload = await response.json();
    if (!response.ok) throw new Error(payload.error || 'Falha ao carregar organizacoes');
    organizations.value = payload.organizations || [];
    if (selectedId.value) {
      const updated = organizations.value.find(item => item.id === selectedId.value);
      if (updated) select(updated);
    }
  } catch (error) {
    message.value = error.message;
  } finally {
    isLoading.value = false;
  }
}

function select(item) {
  const chatwoot = binding(item, 'chatwoot');
  const evolution = binding(item, 'whatsapp_evolution');
  selectedId.value = item.id;
  localStorage.setItem('superwoot-active-organization-id', item.id);
  form.value = {
    name: item.name || '',
    slug: item.slug || '',
    status: item.status || 'active',
    timezone: item.timezone || 'Europe/Lisbon',
    currency: item.default_currency || 'EUR',
    chatwootAccountId: chatwoot.external_account_id || '',
    chatwootInboxId: chatwoot.external_inbox_id || '',
    evolutionInstanceName: evolution.evolution_instance_name || '',
    classificationCombo: item.ai_profile?.classification_combo || '',
    conversationCombo: item.ai_profile?.conversation_combo || '',
    summaryCombo: item.ai_profile?.summary_combo || '',
    imageSimulationCombo: item.ai_profile?.image_simulation_combo || '',
  };
}

function createNew() {
  selectedId.value = '';
  form.value = emptyForm();
  message.value = '';
}

async function save() {
  isSaving.value = true;
  message.value = '';
  try {
    const payload = { ...form.value, tenantId: selectedId.value || undefined };
    const response = await fetch('/api/superwoot/organizations', {
      method: selectedId.value ? 'PATCH' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Falha ao salvar organizacao');
    message.value = 'Organizacao salva.';
    selectedId.value = result.organization.id;
    await load();
  } catch (error) {
    message.value = error.message;
  } finally {
    isSaving.value = false;
  }
}

onMounted(async () => {
  selectedId.value = localStorage.getItem('superwoot-active-organization-id') || '';
  await load();
  if (!selected.value && organizations.value.length === 1) select(organizations.value[0]);
});
</script>

<template>
  <div class="flex h-full min-h-0 bg-n-background">
    <aside class="w-72 shrink-0 overflow-y-auto border-r border-n-weak bg-n-solid-1 p-4">
      <div class="mb-4 flex items-center justify-between gap-3">
        <div>
          <h1 class="text-xl font-medium text-n-slate-12">Organizacoes</h1>
          <p class="mt-1 text-xs text-n-slate-10">Clientes da plataforma compartilhada</p>
        </div>
        <button class="flex h-8 w-8 items-center justify-center rounded-md bg-n-blue-9 text-white" title="Nova organizacao" @click="createNew">
          <span class="i-lucide-plus size-4" />
        </button>
      </div>
      <p v-if="isLoading" class="text-sm text-n-slate-10">Carregando...</p>
      <button
        v-for="item in organizations"
        :key="item.id"
        class="mb-2 w-full rounded-md border px-3 py-3 text-left"
        :class="selectedId === item.id ? 'border-n-blue-8 bg-n-alpha-3' : 'border-n-weak bg-n-solid-2'"
        @click="select(item)"
      >
        <span class="block text-sm font-medium text-n-slate-12">{{ item.name }}</span>
        <span class="mt-1 block text-xs text-n-slate-10">{{ item.slug }} · {{ item.status }}</span>
      </button>
    </aside>

    <main class="min-w-0 flex-1 overflow-y-auto p-6">
      <div class="mx-auto max-w-3xl">
        <div class="mb-5">
          <h2 class="text-lg font-medium text-n-slate-12">{{ selected ? 'Editar organizacao' : 'Nova organizacao' }}</h2>
          <p class="mt-1 text-sm text-n-slate-10">Cada organizacao possui canais, memoria e configuracoes isoladas.</p>
        </div>

        <form class="space-y-6" @submit.prevent="save">
          <section class="space-y-3">
            <h3 class="text-sm font-medium text-n-slate-12">Identificacao</h3>
            <div class="grid gap-3 md:grid-cols-2">
              <label class="block"><span class="mb-1 block text-sm text-n-slate-11">Nome</span><input v-model="form.name" required class="h-9 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" /></label>
              <label class="block"><span class="mb-1 block text-sm text-n-slate-11">Slug</span><input v-model="form.slug" required class="h-9 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" /></label>
              <label class="block"><span class="mb-1 block text-sm text-n-slate-11">Status</span><select v-model="form.status" class="h-9 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm"><option value="active">Ativa</option><option value="suspended">Suspensa</option><option value="archived">Arquivada</option></select></label>
              <label class="block"><span class="mb-1 block text-sm text-n-slate-11">Fuso horario</span><input v-model="form.timezone" required class="h-9 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" /></label>
              <label class="block"><span class="mb-1 block text-sm text-n-slate-11">Moeda</span><input v-model="form.currency" required maxlength="3" class="h-9 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm uppercase" /></label>
            </div>
          </section>

          <section class="space-y-3 border-t border-n-weak pt-5">
            <h3 class="text-sm font-medium text-n-slate-12">Canais</h3>
            <div class="grid gap-3 md:grid-cols-3">
              <label class="block"><span class="mb-1 block text-sm text-n-slate-11">Conta Chatwoot</span><input v-model="form.chatwootAccountId" class="h-9 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" /></label>
              <label class="block"><span class="mb-1 block text-sm text-n-slate-11">Inbox Chatwoot</span><input v-model="form.chatwootInboxId" class="h-9 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" /></label>
              <label class="block"><span class="mb-1 block text-sm text-n-slate-11">Instancia Evolution</span><input v-model="form.evolutionInstanceName" class="h-9 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" /></label>
            </div>
          </section>

          <section class="space-y-3 border-t border-n-weak pt-5">
            <h3 class="text-sm font-medium text-n-slate-12">Combos OmniRoute opcionais</h3>
            <div class="grid gap-3 md:grid-cols-2">
              <label class="block"><span class="mb-1 block text-sm text-n-slate-11">Classificacao</span><input v-model="form.classificationCombo" placeholder="Usar padrao global" class="h-9 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" /></label>
              <label class="block"><span class="mb-1 block text-sm text-n-slate-11">Conversa</span><input v-model="form.conversationCombo" placeholder="Usar padrao global" class="h-9 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" /></label>
              <label class="block"><span class="mb-1 block text-sm text-n-slate-11">Resumo</span><input v-model="form.summaryCombo" placeholder="Usar padrao global" class="h-9 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" /></label>
              <label class="block"><span class="mb-1 block text-sm text-n-slate-11">Simulacao de imagem</span><input v-model="form.imageSimulationCombo" placeholder="Usar padrao global" class="h-9 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" /></label>
            </div>
          </section>

          <div class="flex items-center gap-3 border-t border-n-weak pt-5">
            <button type="submit" :disabled="isSaving" class="rounded-md bg-n-blue-9 px-4 py-2 text-sm font-medium text-white disabled:opacity-60">
              {{ isSaving ? 'Salvando...' : 'Salvar organizacao' }}
            </button>
            <span class="text-sm text-n-slate-10">{{ message }}</span>
          </div>
        </form>
      </div>
    </main>
  </div>
</template>
